import {
  DescriptionRounded,
  DevicesRounded,
  HelpOutlineRounded,
  LockOpenRounded,
  LogoutRounded,
  Person,
  PersonAddRounded,
  SwitchAccountRounded,
} from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Box,
  ButtonBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Hidden from "src/components/Hidden";
import arkadu from "src/coreConfig";
import useAsyncRequest from "src/hooks/useAsyncRequest";
import { toggleHelp } from "src/slices/help";
import { checkPermission, logout, setAuthorized } from "src/slices/users";
import { useDispatch, useSelector } from "src/store";
import axiosInstance from "src/utils/axios";

const Account = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { t } = useTranslation();
  const { user, authorized, identitySelected } = useSelector(
    (state) => state.users,
  );
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setOpen] = useState(false);
  const { api } = useAsyncRequest();
  const { currentCustomer } = useSelector((state) => state.customers);
  const settings = useSelector((state) => state.settings);

  const canRegisterUser =
    currentCustomer &&
    settings.adminMode &&
    checkPermission({
      action: "can_add_user",
      module: "nets_user",
      customerId: currentCustomer.id,
      user: user,
    });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    localStorage.clear();
    try {
      var res = await axiosInstance.get(arkadu.urls.v2.user.logout);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t("No se pudo cerrar sesi�n"), {
        variant: "error",
      });
    }

    try {
      handleClose();
      dispatch(logout());
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t("No se pudo cerrar sesi�n"), {
        variant: "error",
      });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Box
        component={ButtonBase}
        onClick={handleOpen}
        ref={ref}
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Badge
          badgeContent={
            authorized ? (
              <LockOpenRounded size="small" sx={{ height: 16, width: 16 }} />
            ) : (
              0
            )
          }
          color="success"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <>
            <Avatar alt="User" src={user.avatar} />
            <Hidden lgDown>
              <Typography variant="h6" color="inherit">
                {user.name}
              </Typography>
            </Hidden>
          </>
        </Badge>
      </Box>
      <Menu
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        anchorEl={ref.current}
        open={isOpen}
      >
        <MenuItem
          onClick={() => {
            navigate("/app/account");
            handleClose();
          }}
        >
          <ListItemIcon>
            <Person color="info" />
          </ListItemIcon>
          <ListItemText>{t("Cuenta")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/app/generated-documents/");
            handleClose();
          }}
        >
          <ListItemIcon>
            <DescriptionRounded color="info" />
          </ListItemIcon>
          <ListItemText
            primary={t("Mis PDF")}
            secondary={t("Documentos generados y pendientes")}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/app/account/security");
            handleClose();
          }}
        >
          <ListItemIcon>
            <DevicesRounded color="info" />
          </ListItemIcon>
          <ListItemText
            primary={t("Sesiones activas")}
            secondary={t("Dispositivos conectados")}
          />
        </MenuItem>
        {!settings.adminMode && (
          <MenuItem
            onClick={() => {
              navigate("/app/account/selectIdentity/");
              handleClose();
            }}
          >
            <ListItemIcon>
              <SwitchAccountRounded color="info" />
            </ListItemIcon>
            <ListItemText
              primary={t("Cambiar identidad")}
              secondary={
                identitySelected
                  ? identitySelected?.relation_type === "shared"
                    ? t(
                        "Su sesi�n est� anclada a una identidad compartida. Pulse aqu� para cambiarla o configurarla.",
                      )
                    : t(
                        "Su sesi�n est� anclada a una identidad. Pulse aqu� para cambiarla o configurarla.",
                      )
                  : t("Seleccione una identidad para operar en modo usuario")
              }
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            dispatch(toggleHelp(true));
            handleClose();
          }}
        >
          <ListItemIcon>
            <HelpOutlineRounded color="info" />
          </ListItemIcon>
          <ListItemText
            primary={t("Ayuda")}
            secondary={t("Gu�a r�pida de uso del sistema")}
          />
        </MenuItem>
        {authorized && (
          <MenuItem
            onClick={() => {
              dispatch(setAuthorized(false));
              handleClose();
            }}
          >
            <ListItemIcon>
              <LockOpenRounded color="info" />
            </ListItemIcon>
            <ListItemText>{t("Bloquear")}</ListItemText>
          </MenuItem>
        )}
        {canRegisterUser && (
          <MenuItem
            onClick={() => {
              handleClose();
              window.open(
                `/${currentCustomer.shortname}/admin/user/registration/?mode=assisted&customer_id=${currentCustomer.id}`,
                "_blank",
              );
            }}
          >
            <ListItemIcon>
              <PersonAddRounded color="info" />
            </ListItemIcon>
            <ListItemText>{t("Registrar Usuario")}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutRounded color="primary" />
          </ListItemIcon>
          <ListItemText>{t("Cerrar sesi�n")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default Account;
